<template>
  <v-container style="background-color: #104B94;" class="fill-height" fluid>
    <overlay :value="loading"></overlay>
    <v-container class="d-flex flex-column justify-center align-center fill-height">
      <v-card class="ma-0 ma-md-6 pa-8" width="100%" max-width="480px">
        <v-img class="mb-4" src="@/assets/new_Pawer_Logo.png" height="50" contain></v-img>

        <!-- Step 1: scan the QR and confirm a code -->
        <v-card-text v-if="!recoveryCodes.length" class="ma-0 pa-0">
          <h3 class="text-center mb-2">Configura tu verificación en dos pasos</h3>
          <p class="text-body-2 text-center mb-4">
            Escanea este código con Google Authenticator (o una app equivalente) y luego ingresa el
            código de 6 dígitos que te muestre.
          </p>

          <div v-if="qrCode" class="d-flex justify-center mb-4">
            <img :src="qrCode" alt="Código QR para vincular la aplicación de autenticación" width="200" height="200" />
          </div>

          <p class="text-caption text-center mb-1">¿No puedes escanear? Ingresa esta clave manualmente:</p>
          <p class="text-center font-weight-bold mb-4" style="word-break: break-all;">{{ manualEntryKey }}</p>

          <!-- Same single-field implicit-submission trap as MfaVerify.vue: without
               @submit.prevent, Enter reloads the page mid-request. -->
          <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="validate">
            <v-text-field
              v-model="code"
              :rules="codeRules"
              label="Código de 6 dígitos"
              maxlength="6"
              inputmode="numeric"
              required
              :disabled="loading"
              @keydown.enter.prevent="validate"
            ></v-text-field>

            <v-alert v-if="error" dense type="error">{{ errorMessage }}</v-alert>

            <div class="d-flex justify-center">
              <v-btn
                type="button"
                color="primary"
                class="mt-4"
                rounded
                :loading="loading"
                :disabled="loading"
                @click="validate"
              >Verificar</v-btn>
            </div>
          </v-form>
        </v-card-text>

        <!-- Step 2: recovery codes, shown exactly once -->
        <v-card-text v-else class="ma-0 pa-0">
          <h3 class="text-center mb-2">Guarda tus códigos de recuperación</h3>
          <v-alert dense type="warning" class="mb-4">
            Estos códigos no se volverán a mostrar. Guárdalos en un lugar seguro: te permitirán
            ingresar si pierdes tu teléfono.
          </v-alert>

          <v-simple-table dense class="mb-4">
            <tbody>
              <tr v-for="(recoveryCode, index) in recoveryCodes" :key="index">
                <td class="text-center font-weight-bold">{{ recoveryCode }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="d-flex justify-space-between">
            <v-btn text rounded @click="copyCodes">{{ copied ? 'Copiado' : 'Copiar códigos' }}</v-btn>
            <v-btn color="primary" rounded @click="continueToPanel">Continuar</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-container>
</template>

<script>
import Overlay from '../components/Overlay.vue';
import MfaService from '../services/mfa.service';

export default {
  components: { Overlay },
  data: () => ({
    valid: true,
    loading: false,
    error: false,
    errorMessage: '',
    copied: false,
    qrCode: '',
    manualEntryKey: '',
    code: '',
    codeRules: [
      v => !!v || 'El código es requerido',
      v => /^[0-9]{6}$/.test(v) || 'El código debe tener 6 dígitos'
    ],
    recoveryCodes: []
  }),
  async created() {
    if (!MfaService.getMfaToken()) {
      this.$router.push({ path: '/login' });
      return;
    }
    await this.loadQr();
  },
  methods: {
    async loadQr() {
      this.loading = true;
      const response = await MfaService.setup();
      if (response.status === 'ok') {
        this.qrCode = response.data.qrCode;
        this.manualEntryKey = response.data.manualEntryKey;
      } else if (response.code === 'MFA_ALREADY_ENROLLED') {
        this.$router.push({ path: '/mfa/verify' });
      } else {
        this.showError(response.message);
      }
      this.loading = false;
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.verify();
      }
    },
    async verify() {
      this.loading = true;
      this.error = false;
      const response = await MfaService.verify(this.code);
      if (response.status === 'ok') {
        // A recovery code returns no `data` — it hands back a fresh enrollment
        // token instead. Unreachable from this view (the field only accepts 6
        // digits), but reading `.data` blindly would throw if that ever changes.
        if (response.nextStep === 'mfa_setup') {
          await this.loadQr();
          this.code = '';
          this.loading = false;
          return;
        }
        this.recoveryCodes = (response.data && response.data.recoveryCodes) || [];
        if (!this.recoveryCodes.length) {
          this.continueToPanel();
        }
      } else {
        this.code = '';
        this.showError(response.message);
      }
      this.loading = false;
    },
    async copyCodes() {
      try {
        await navigator.clipboard.writeText(this.recoveryCodes.join('\n'));
        this.copied = true;
      } catch (err) {
        this.showError('No se pudieron copiar los códigos. Cópialos manualmente.');
      }
    },
    continueToPanel() {
      // `/` resolves the admin's default section (router guard, spec 028).
      this.$router.push({ path: '/' });
    },
    showError(message) {
      this.error = true;
      this.errorMessage = message || 'Ocurrió un error, intente nuevamente';
    }
  }
};
</script>
